import { useState, useCallback } from 'react';
import { cargaMasivaDietasApi } from '../api/cargaMasivaDietasApi';
import { handleApiError, showErrorToast, showSuccessToast, showWarningToast } from '../utils/errorHandler';

/**
 * Hook personalizado para carga masiva de dietas
 * Proporciona estado y funciones para procesar archivos Excel de dietas
 */
export const useCargaMasivaDietas = () => {
  const [loading, setLoading] = useState(false); // ✅ AGREGAR: Estado loading faltante
  const [error, setError] = useState(null);
  const [resultados, setResultados] = useState(null);
  const [tiposDieta, setTiposDieta] = useState([]);

  /**
   * Procesa la carga masiva de dietas desde archivo Excel
   * @param {File} file - Archivo Excel (.xlsx, .xls)
   * @param {number} idProyecto - ID del proyecto para asociar las dietas
   */
  const procesarCargaMasiva = useCallback(async (file, idProyecto) => {
    setLoading(true);
    setError(null);
    setResultados(null);
    
    try {
      const response = await cargaMasivaDietasApi.procesarCargaMasiva(file, idProyecto);
      const data = response.data;
      
      // Si hay errores de validación (422), mostrar detalles
      if (response.status === 422 || (data.success === false && data.resultados)) {
        setResultados(data.resultados);
        const { validos, errores, detalles_errores } = data.resultados;
        
        if (detalles_errores && detalles_errores.length > 0) {
          const erroresTexto = detalles_errores
            .map(err => `Fila ${err.fila}: ${err.errores.join(', ')}`)
            .join('\n');
          showErrorToast(
            `Errores de validación en el archivo:\n${erroresTexto}`,
            'Errores de validación'
          );
        } else {
          showWarningToast(
            `⚠️ Carga completada con errores: ${validos} válidos, ${errores} errores`
          );
        }
        
        return data;
      }
      
      // Si es exitoso
      setResultados(data.resultados);
      
      // Mostrar toast de resumen
      const {
        nuevos,
        actualizados,
        errores,
        omitidos_dieta_activa: omitidosDieta = 0,
        total_procesados: totalProc = 0,
        bloqueo_documento_sin_comensal: bloqueoSinComensal,
        mensaje_bloqueo: mensajeBloqueo,
      } = data.resultados;
      const exitosas = nuevos + actualizados + (data.resultados.cierre_dietas || 0);

      if (bloqueoSinComensal) {
        showErrorToast(
          mensajeBloqueo ||
            'No se guardó ningún registro. Corrija los documentos sin comensal registrado y vuelva a cargar.',
          'Carga no aplicada'
        );
        return data;
      }

      if (errores === 0) {
        const omitidosTxt =
          omitidosDieta > 0
            ? `, ${omitidosDieta} omitido(s) (ya tenían dieta activa)`
            : '';
        showSuccessToast(
          `✅ Carga completada (${totalProc} filas): ${nuevos} nueva(s), ${actualizados} actualizada(s)${omitidosTxt}`
        );
      } else {
        showWarningToast(
          `⚠️ Carga con errores (${totalProc} filas): ${exitosas} exitosa(s), ${errores} error(es)${
            omitidosDieta > 0 ? `, ${omitidosDieta} omitido(s) por dieta activa` : ''
          }`
        );
      }
      
      return data;
    } catch (err) {
      const errorMessage = handleApiError(err, 'Error en carga masiva de dietas');
      setError(errorMessage);
      
      // Si el error tiene detalles de validación
      if (err.response?.status === 422 && err.response?.data?.resultados) {
        setResultados(err.response.data.resultados);
      }
      
      showErrorToast(err, 'Error en carga masiva de dietas');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtiene información de tipos de dieta para mostrar en la plantilla
   */
  const fetchTiposDieta = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await cargaMasivaDietasApi.obtenerTiposDieta();
      const tipos = response.data.data || [];
      setTiposDieta(tipos);
      return tipos;
    } catch (err) {
      const errorMessage = handleApiError(err, 'Error al cargar tipos de dieta');
      setError(errorMessage);
      showErrorToast(err, 'Error al cargar tipos de dieta');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Valida los datos del Excel antes de enviar
   */
  const validarDatosExcel = useCallback((datos) => {
    const errores = [];
    
    if (!Array.isArray(datos) || datos.length === 0) {
      errores.push('El archivo debe contener al menos una fila de datos');
      return { valido: false, errores };
    }

    datos.forEach((fila, index) => {
      const numeroFila = index + 2; // +2 porque Excel empieza en 1 y saltamos headers
      
      // Validar campos obligatorios
      if (!fila.numero_documento_comedor || fila.numero_documento_comedor.toString().trim() === '') {
        errores.push(`Fila ${numeroFila}: Número de documento de comedor es obligatorio`);
      } else {
        // Validar formato de documento de comedor
        const numeroDoc = fila.numero_documento_comedor.toString().trim();
        if (/^\d{8}$/.test(numeroDoc)) {
          // Es un DNI válido de 8 dígitos
        } else if (numeroDoc.length < 6 || numeroDoc.length > 15) {
          errores.push(`Fila ${numeroFila}: Documento de comedor inválido. Si es DNI debe tener 8 dígitos, otros documentos entre 6-15 caracteres`);
        }
      }
      
      if (!fila.id_tipo_dieta || isNaN(parseInt(fila.id_tipo_dieta))) {
        errores.push(`Fila ${numeroFila}: ID de tipo de dieta es obligatorio y debe ser numérico`);
      }
      
      if (!fila.fecha_inicio || fila.fecha_inicio.toString().trim() === '') {
        errores.push(`Fila ${numeroFila}: Fecha de inicio es obligatoria`);
      }
      
      // Validar formato de fechas
      const formatoFecha = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
      
      if (fila.fecha_inicio && !formatoFecha.test(fila.fecha_inicio.toString().trim())) {
        errores.push(`Fila ${numeroFila}: Fecha de inicio debe tener formato DD/MM/YYYY`);
      }
      
      // Solo validar fecha_fin si realmente tiene contenido válido
      if (fila.fecha_fin !== null && fila.fecha_fin !== undefined && fila.fecha_fin !== '') {
        const fechaFinStr = String(fila.fecha_fin).trim();
        // Solo validar si después de limpiar no está vacía y no es 'undefined' como string
        if (fechaFinStr && fechaFinStr !== '' && fechaFinStr !== 'undefined' && fechaFinStr !== 'null') {
          if (!formatoFecha.test(fechaFinStr)) {
            errores.push(`Fila ${numeroFila}: Fecha de fin debe tener formato DD/MM/YYYY`);
          }
        }
      }
    });

    return {
      valido: errores.length === 0,
      errores
    };
  }, []);

  /**
   * Genera texto de ayuda para tipos de dieta
   */
  const generarAyudaTiposDieta = useCallback(() => {
    if (tiposDieta.length === 0) return '';
    
    return tiposDieta.map(tipo => 
      `${tipo.id}: ${tipo.nombre} - ${tipo.descripcion}`
    ).join('\n');
  }, [tiposDieta]);

  /**
   * Limpia el estado de resultados
   */
  const limpiarResultados = useCallback(() => {
    setResultados(null);
    setError(null);
  }, []);

  /**
   * Limpia el estado de error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Limpia todos los estados
   */
  const clearState = useCallback(() => {
    setResultados(null);
    setTiposDieta([]);
    setError(null);
    setLoading(false);
  }, []);

  return {
    // Estado
    loading,
    error,
    resultados,
    tiposDieta,
    
    // Funciones principales
    procesarCargaMasiva,
    fetchTiposDieta,
    
    // Funciones de utilidad
    validarDatosExcel,
    generarAyudaTiposDieta,
    limpiarResultados,
    clearError,
    clearState
  };
};

export default useCargaMasivaDietas;
